<template>
  <b-form-input
    v-bind="$attrs"
    :value="value"
    v-on="forwardedListeners"
    @input="onInput"
    @keydown="onKeydown"
    @focus="onFocus"
    @click="onClick"
  />
</template>

<script>
/**
 * Input de texto común con comportamientos de interacción unificados.
 * Conserva la API de b-form-input y, por defecto, selecciona todo el texto
 * al enfocarse con Tab o al hacer clic, facilitando reemplazar el valor.
 */
export default {
  name: 'AppTextInput',
  inheritAttrs: false,
  props: {
    value: { type: [String, Number], default: '' },
    selectOnFocus: { type: Boolean, default: true },
    // Expresión de los caracteres permitidos. Por ejemplo /[0-9]/g
    // conserva únicamente dígitos, incluso cuando el usuario pega texto.
    allowedPattern: { type: [RegExp, String], default: null },
  },
  computed: {
    forwardedListeners() {
      // Estos tres eventos se controlan acá para mantener el v-model y la
      // selección automática, pero se reenvían a quien use el componente.
      // eslint-disable-next-line no-unused-vars
      const { input, keydown, focus, click, ...rest } = this.$listeners
      return rest
    },
  },
  methods: {
    // Mantiene la misma API imperativa de b-form-input para usos mediante ref.
    focus() {
      if(this.$el && typeof this.$el.focus === 'function') {
        this.$el.focus()
      }
    },
    select() {
      this.selectText(this.$el)
    },
    callListener(name, event) {
      var listener = this.$listeners[name]
      if(Array.isArray(listener)) {
        listener.forEach((fn) => fn(event))
      } else if(typeof listener === 'function') {
        listener(event)
      }
    },
    onInput(value) {
      var normalized = this.filterAllowedCharacters(value)
      if(normalized !== value && this.$el) {
        this.$el.value = normalized
      }
      this.$emit('input', normalized)
    },
    onKeydown(event) {
      this.callListener('keydown', event)
      if(event.defaultPrevented || !this.allowedPattern) return
      if(event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) return
      if(this.filterAllowedCharacters(event.key) !== event.key) {
        event.preventDefault()
      }
    },
    onFocus(event) {
      this.callListener('focus', event)
      this.selectText(event.target)
    },
    onClick(event) {
      this.callListener('click', event)
      // En un click el navegador puede ubicar el cursor después de focus;
      // se difiere la selección para que el comportamiento sea consistente.
      this.$nextTick(() => this.selectText(event.target))
    },
    selectText(input) {
      if(!this.selectOnFocus || !input || typeof input.select !== 'function') {
        return
      }
      input.select()
    },
    filterAllowedCharacters(value) {
      if(!this.allowedPattern) return value
      var pattern = this.allowedPattern instanceof RegExp
        ? this.allowedPattern
        : new RegExp(this.allowedPattern, 'g')
      var flags = pattern.flags.indexOf('g') === -1 ? pattern.flags + 'g' : pattern.flags
      var matches = String(value || '').match(new RegExp(pattern.source, flags))
      return matches ? matches.join('') : ''
    },
  },
}
</script>
